/**
 * Shape of builder `Language` rows used here — defined locally so `_default` can publish
 * without importing `src/store` (store `Language` is assignable to this).
 */
export type LanguageLike = {
  id?: number;
  /** V2 `/languageSettings` flat row */
  name?: string;
  icon?: string;
  languageCode?: string;
  locale?: string;
  languageShortname?: string;
  language?: {
    id?: number;
    name?: string;
    shortname?: string;
    icon?: string;
    icon_url?: string;
  } | null;
};

/** Lowercase trimmed form for comparisons only. */
export function normalizeLanguageCode(
  code: string | null | undefined
): string {
  return String(code ?? "")
    .trim()
    .toLowerCase();
}

export function languageCodesEqual(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  return normalizeLanguageCode(a) === normalizeLanguageCode(b);
}

/** Canonical code string for a site language row (for matching to webpage.languageCode). */
export function languageCodeFromActiveLanguage(
  lang: LanguageLike | null | undefined
): string {
  if (!lang) return "";
  const top =
    (typeof lang.languageCode === "string" && lang.languageCode.trim()) ||
    (typeof lang.locale === "string" && lang.locale.trim()) ||
    (typeof lang.languageShortname === "string" &&
      lang.languageShortname.trim()) ||
    "";
  if (top) return top;
  const short = lang.language?.shortname;
  return typeof short === "string" ? short : "";
}

/** UI label: V2 `name`, nested `language.name`, else canonical code. */
export function displayNameFromActiveLanguage(
  lang: LanguageLike | null | undefined
): string {
  if (!lang) return "";
  if (typeof lang.name === "string") {
    const t = lang.name.trim();
    if (t) return t;
  }
  const nested = lang.language?.name;
  if (typeof nested === "string" && nested.trim()) return nested.trim();
  return languageCodeFromActiveLanguage(lang);
}

/** Estonian site languages: `et`, `et_EE`, etc. */
export function isEstonianLanguageCode(
  code: string | null | undefined
): boolean {
  const n = normalizeLanguageCode(code).replace(/-/g, "_");
  if (n === "et") return true;
  return n.startsWith("et_");
}

/** Russian site languages: `ru`, `ru_RU`, etc. */
export function isRussianLanguageCode(
  code: string | null | undefined
): boolean {
  const n = normalizeLanguageCode(code).replace(/-/g, "_");
  if (n === "ru") return true;
  return n.startsWith("ru_");
}

/** English site languages: `en`, `en_GB`, `en-US`, etc. */
export function isEnglishLanguageCode(
  code: string | null | undefined
): boolean {
  const n = normalizeLanguageCode(code).replace(/-/g, "_");
  if (n === "en") return true;
  return n.startsWith("en_");
}

export function findActiveLanguageByCode<T extends LanguageLike>(
  activeLanguages: T[] | undefined | null,
  code: string | null | undefined
): T | undefined {
  if (!activeLanguages?.length || !normalizeLanguageCode(code)) {
    return undefined;
  }
  return activeLanguages.find((lang) =>
    languageCodesEqual(languageCodeFromActiveLanguage(lang), code)
  );
}
